import { OverageStatus, ServiceLineStatus, ServiceLineType } from "@/shared/types/serviceLineTypes";
import { colorKeyFromMap, getBadgeColorClasses, getTextColorClass, getChartFill, TailwindColorKey } from "./color-utils";

/** Service Line Status Value */
export const SERVICE_LINE_STATUS_VALUE = {
    [ServiceLineStatus.ACTIVE]: 'Active',
    [ServiceLineStatus.EXHAUSTED]: 'Exhausted',
    [ServiceLineStatus.PAUSED]: 'Paused',
    [ServiceLineStatus.SUSPENDED]: 'Suspended',
    [ServiceLineStatus.NO_UT]: 'Standby',
    [ServiceLineStatus.OFFLINE]: 'Offline',
};

/** Service Line Status palette keys */
const SERVICE_LINE_STATUS_COLORS: Record<ServiceLineStatus, TailwindColorKey> = {
    [ServiceLineStatus.ACTIVE]: 'green',
    [ServiceLineStatus.EXHAUSTED]: 'orange',
    [ServiceLineStatus.SUSPENDED]: 'black',
    [ServiceLineStatus.PAUSED]: 'blue',
    [ServiceLineStatus.NO_UT]: 'gray',
    [ServiceLineStatus.OFFLINE]: 'red',
};

export const getServiceLineStatusColorKey = (status: ServiceLineStatus): TailwindColorKey =>
    colorKeyFromMap(SERVICE_LINE_STATUS_COLORS, status);

export const getServiceLineStatusBGColorClass = (status: ServiceLineStatus): string =>
    getBadgeColorClasses(getServiceLineStatusColorKey(status));

export const getServiceLineStatusTextColorClass = (status: ServiceLineStatus): string =>
    getTextColorClass(getServiceLineStatusColorKey(status));

export const getServiceLineStatusChartFill = (status: ServiceLineStatus): string =>
    getChartFill(getServiceLineStatusColorKey(status));

/** Service Line Types Badge Colors */
export const SERVICE_LINE_TYPE_VALUES = {
    [ServiceLineType.PRIMARY]: 'Primary',
    [ServiceLineType.BACKUP]: 'Backup',
} as const;

const SERVICE_LINE_TYPE_COLORS: Record<ServiceLineType, TailwindColorKey> = {
    [ServiceLineType.PRIMARY]: 'blue',
    [ServiceLineType.BACKUP]: 'gray',
};

export const getServiceLineTypeColorKey = (type: ServiceLineType): TailwindColorKey =>
    colorKeyFromMap(SERVICE_LINE_TYPE_COLORS, type);

export const getServiceLineTypeBGColorClass = (type: ServiceLineType): string =>
    getBadgeColorClasses(getServiceLineTypeColorKey(type));

export const getServiceLineTypeTextColorClass = (type: ServiceLineType): string =>
    getTextColorClass(getServiceLineTypeColorKey(type));

export const getServiceLineTypeChartFill = (type: ServiceLineType): string =>
    getChartFill(getServiceLineTypeColorKey(type));

/** Service Line Overage Status Badge Colors */
export const SERVICE_LINE_OVERAGE_STATUSES = {
    [OverageStatus.ACTIVE]: 'Active',
    [OverageStatus.EXPIRED]: 'Expired',
    [OverageStatus.NO_OVERAGE]: 'No Overage',
} as const;

const SERVICE_LINE_OVERAGE_COLORS: Record<OverageStatus, TailwindColorKey> = {
    [OverageStatus.ACTIVE]: 'green',
    [OverageStatus.EXPIRED]: 'red',
    [OverageStatus.NO_OVERAGE]: 'yellow',
};

export const getOverageStatusColorKey = (status: OverageStatus): TailwindColorKey =>
    colorKeyFromMap(SERVICE_LINE_OVERAGE_COLORS, status);

export const getOverageStatusBGColorClass = (status: OverageStatus): string =>
    getBadgeColorClasses(getOverageStatusColorKey(status));

export const getOverageStatusTextColorClass = (status: OverageStatus): string =>
    getTextColorClass(getOverageStatusColorKey(status));